import { Alert, AlertDescription, AlertIcon, Button, Flex, FormControl, FormLabel, Input, Stack } from '@chakra-ui/react';
import { FormView } from '@resistapp/client/components/shared/layout';
import { ErrorDiv } from '@resistapp/client/components/states/error';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { getUserProjectIds } from '@resistapp/common/access';
import { isAdmin, isNonDemo, resistomapOrganisationId } from '@resistapp/common/features';
import { OrganisationUserRole } from '@resistapp/common/types';
import { cityDemoProjectId, oldDemoProjectIds } from '@resistapp/common/utils';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';
import { AgreePoliciesCheckbox } from '../components/agree-policies-checkbox/agree-policies-checkbox';
import { ResistomapButton } from '../components/buttons/resistomap-button';
import { RSecondTitle } from '../components/headers/r-second-title';
import { LocalStorageId } from '../components/shared/local-storage';
import { usePasswordResetRequest } from '../hooks/api';
import { useLabWorkbookLanding } from '../hooks/use-lab-workbook-landing';
import { useNavigateWithQuery } from '../hooks/use-navigate-with-query';
import { useToastWithoutDebounce } from '../hooks/use-resistomap-toast';
import { initHotjar } from '../hotjar';
import { shouldForceLabLanding } from '../utils/access';
import { parseLoginReturnPath } from '../utils/login-return-path';
import { getBestPageForProject } from '../utils/project-page-access';

export function LoginView() {
  const { user, loginErrorMessage, login, loggingIn } = useUser();
  const forceLabLanding = shouldForceLabLanding(user);
  const {
    path: labLandingPath,
    organisationId: labLandingOrganisationId,
    ensureQuery: labLandingQuery,
  } = useLabWorkbookLanding(user, { enabled: forceLabLanding });
  const labLandingPending = Boolean(forceLabLanding && labLandingOrganisationId && labLandingQuery.isPending);
  const [cookiesAccepted, setCookiesAccepted] = useLocalStorageState(LocalStorageId.cookiesAccepted, {
    defaultValue: false,
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotError, setForgotError] = useState<string | undefined>();
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const returnPath = useMemo(() => parseLoginReturnPath(location.search), [location.search]);
  const { create: requestReset } = usePasswordResetRequest();
  const showToast = useToastWithoutDebounce();

  // This handles the redirection of the user to the correct place after successful login has been done (ie. we have user-property)
  useEffect(() => {
    if (!user) {
      initHotjar();
      return;
    }

    if (labLandingPending) {
      return;
    }

    if (returnPath) {
      navigate(returnPath, false, true);
      return;
    }

    const userProjectIds = getUserProjectIds(user);
    if (forceLabLanding && userProjectIds.length === 0 && labLandingPath) {
      navigate(labLandingPath, false);
      return;
    }

    if (forceLabLanding) {
      navigate('/admin/lab-workbooks', false);
      return;
    }

    const isOrganisationAdmin = user.organisations.some(
      organisation => organisation.membershipRole === OrganisationUserRole.ADMIN,
    );
    if (isAdmin(user) || isOrganisationAdmin) {
      navigate('/admin', false);
      return;
    }

    const projectIds = userProjectIds;
    const demoProjectIds = new Set<number>([cityDemoProjectId, ...Object.values(oldDemoProjectIds)]);
    const nonDemoProjectIds = projectIds.filter(projectId => !demoProjectIds.has(projectId));
    const defaultProjectId = nonDemoProjectIds[0] ?? projectIds[0];

    if (isNonDemo(user) && user.organisations.length === 1 && nonDemoProjectIds.length === 0) {
      const targetOrganisationId =
        user.organisations.find(org => !org.isDemo && org.id !== resistomapOrganisationId)?.id ??
        user.organisations[0]?.id;
      if (targetOrganisationId) {
        navigate(`/smartchip-spec/${targetOrganisationId}`, false);
        return;
      }
    }

    if (projectIds.length && defaultProjectId) {
      navigate(`/${getBestPageForProject(user, defaultProjectId)}/${defaultProjectId}`, false);
    } else {
      console.error(`User ${user.id} has no projects`);
      navigate(`/`, false);
    }
  }, [user, navigate, labLandingPath, labLandingPending, forceLabLanding, returnPath]); // navigate is stable

  const loginDisabled = !email || !password || !cookiesAccepted || loggingIn || !!user;

  const onForgotPassword = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setForgotError('Please enter valid email');
      return;
    }
    setForgotError(undefined);
    try {
      await requestReset.mutateAsync({ email });
      showToast({
        title: 'Password reset email has been sent if the email is registered in the system.',
        status: 'success',
      });
    } catch (error) {
      showToast({ title: 'Failed to send password reset email', status: 'error' });
    }
  };

  return (
    <FormView>
      <RSecondTitle>Login</RSecondTitle>
      <Stack spacing={5} width="full">
        {returnPath ? (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              You do not have access to the requested resource. Please log in with appropriate credentials.
            </AlertDescription>
          </Alert>
        ) : null}
        <FormControl isRequired>
          <FormLabel htmlFor="email">Email</FormLabel>
          <Input
            value={email}
            onChange={event => {
              setEmail(event.target.value);
            }}
            size="md"
            id="email"
            placeholder="Email"
            name="email"
            autoComplete="email"
            width="full"
            isRequired
            autoFocus
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel htmlFor="password">Password</FormLabel>
          <Input
            value={password}
            onChange={event => {
              setPassword(event.target.value);
            }}
            size="md"
            isRequired
            width="full"
            name="password"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            autoFocus
          />
        </FormControl>
        <Flex width="full" justify="space-between" transform="translate(0,-20px)">
          <Button
            variant="ghost"
            bg="transparent"
            size="sm"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            onClick={() => {
              setShowPassword(prev => !prev);
            }}
          >
            {showPassword ? 'Hide password' : 'Show password'}
          </Button>
          <Button
            variant="ghost"
            bg="transparent"
            size="sm"
            _hover={{ bg: 'transparent', color: 'black' }}
            _active={{ bg: 'transparent' }}
            onClick={() => {
              void onForgotPassword();
            }}
          >
            Forgot password?
          </Button>
        </Flex>
        {forgotError && <ErrorDiv>{forgotError}</ErrorDiv>}
        <AgreePoliciesCheckbox
          cookiesAccepted={cookiesAccepted}
          setCookiesAccepted={setCookiesAccepted}
          disabled={loggingIn || !!user}
        />
        <ResistomapButton
          style={{ textTransform: 'capitalize', width: '100%' }}
          onClick={() => {
            login(email, password);
          }}
          isDisabled={loginDisabled}
        >
          {loggingIn ? 'Logging in...' : 'Log in'}
        </ResistomapButton>
        {loginErrorMessage && <ErrorDiv>{loginErrorMessage}</ErrorDiv>}
      </Stack>
    </FormView>
  );
}
